/**
 * API 基础配置和工具函数
 */

import { logDebug, logError, logInfo } from '../utils/errorHandler';

// 优先使用环境变量，否则使用当前域名的 /api
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:8080/api');

/**
 * 统一请求方法
 */
export async function request<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  logDebug('[API] Request', {
    method: options?.method || 'GET',
    url,
    headers: options?.headers,
    body: options?.body
  });
  
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = await fetch(url, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options?.headers,
      },
    });

    logDebug('[API] Response', {
      url,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError('[API] Response not OK', {
        url,
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      const error = new Error(`HTTP ${response.status} ${response.statusText}\nURL: ${url}\n响应: ${errorText}`);
      // 自动显示错误弹窗
      import('../utils/errorManager').then(({ showGlobalError }) => {
        showGlobalError(error.message, '请求失败');
      }).catch(() => {
        // fallback
        console.error('[API] Failed to show error modal');
      });
      throw error;
    }

    const result = await response.json();
    logInfo('[API] Response JSON parsed', {
      url,
      code: result.code,
      message: result.message,
      hasData: !!result.data,
      dataKeys: result.data ? Object.keys(result.data) : [],
      dataType: typeof result.data
    });
    logDebug('[API] Response data', { url, code: result.code, message: result.message });
    
    // 处理统一响应格式
    if (result.code !== 0) {
      logError('[API] API error', {
        url,
        code: result.code,
        message: result.message,
        fullResponse: result
      });
      const errorMessage = `API 错误: ${result.message || '请求失败'} (code: ${result.code})\nURL: ${url}`;
      const error = new Error(errorMessage) as any;
      // 在错误对象上附加响应数据，方便调用方访问（如订单记录）
      error.responseData = result.data;
      error.responseCode = result.code;
      error.responseMessage = result.message;
      // 自动显示错误弹窗
      import('../utils/errorManager').then(({ showGlobalError }) => {
        showGlobalError(errorMessage, '请求失败');
      }).catch(() => {
        // fallback
        console.error('[API] Failed to show error modal');
      });
      throw error;
    }

    logInfo('[API] Request successful, returning data', {
      url,
      code: result.code,
      hasData: !!result.data,
      dataKeys: result.data ? Object.keys(result.data) : []
    });
    return result.data;
  } catch (error: any) {
    logError('[API] Request failed', { 
      url, 
      error,
      errorType: error?.constructor?.name,
      errorMessage: error?.message,
      errorStack: error?.stack?.split('\n').slice(0, 3).join('\n')
    });
    throw error;
  }
}

/**
 * GET 请求
 */
export function get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
  let url = endpoint;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        searchParams.append(key, String(params[key]));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  return request<T>(url);
}

/**
 * POST 请求
 */
export function post<T>(endpoint: string, data?: any): Promise<T> {
  return request<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * PUT 请求
 */
export function put<T>(endpoint: string, data?: any): Promise<T> {
  return request<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * PATCH 请求
 */
export function patch<T>(endpoint: string, data?: any): Promise<T> {
  return request<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * DELETE 请求
 */
export function del<T>(endpoint: string): Promise<T> {
  return request<T>(endpoint, {
    method: 'DELETE',
  });
}

